import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { ChevronLeft, ChevronRight, CalendarDays, MapPin, Clock, Loader2, UserX, UserCheck } from "lucide-react";
import { fmtH } from "../lib/planning";

const DAY_NAMES = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const DAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const fmtDay = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };
const fmtRange = (days) => `${fmtDay(days[0])} – ${fmtDay(days[6])}`;
const addDays = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export function WeekEntry({ e, dense = false }) {
    const absent = e.status === "assente";
    const sub = e.status === "sostituto";
    return (
        <div className={`rounded-md border px-2.5 py-2 ${absent ? "border-red-200 bg-red-50/70" : sub ? "border-sky-200 bg-sky-50/70" : "border-slate-200 bg-white"}`} data-testid="week-entry">
            <div className="flex items-center justify-between gap-2">
                <span className={`inline-flex items-center gap-1 font-mono-numbers text-xs font-bold ${absent ? "text-red-600 line-through" : "text-[#376DB3]"}`}>
                    <Clock className="h-3 w-3" /> {e.start}–{e.end}
                </span>
                {!dense && <span className="text-[10px] text-slate-400">{fmtH(e.hours)} h</span>}
            </div>
            <div className={`mt-0.5 truncate text-sm font-semibold ${absent ? "text-slate-500" : "text-slate-800"}`} title={e.worksite_name}>{e.worksite_name}</div>
            {e.client_name && <div className="truncate text-[11px] text-slate-500" title={e.client_name}>{e.client_name}</div>}
            <div className="truncate text-[11px] text-slate-600">{e.activity_name}</div>
            {absent && (
                <div className="mt-1 inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid="week-entry-absent">
                    <UserX className="h-3 w-3" /> Assente · {e.reason || "—"}{e.sub_by ? ` · ti sostituisce ${e.sub_by}` : ""}
                </div>
            )}
            {sub && (
                <div className="mt-1 inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700" data-testid="week-entry-sub">
                    <UserCheck className="h-3 w-3" /> Sostituisci {e.sub_for}{e.reason ? ` (${e.reason.toLowerCase()})` : ""}
                </div>
            )}
        </div>
    );
}

/**
 * "La mia settimana": cantieri, giorni e orari del dipendente. Si nasconde da sola se non ci sono assegnazioni.
 * variant "portal" (card chiara) | "timbratura" (riquadro "Oggi" con toggle settimana).
 */
export function MyWeekCard({ variant = "portal", className = "" }) {
    const [start, setStart] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showWeek, setShowWeek] = useState(variant === "portal");

    const load = useCallback(async (s) => {
        setLoading(true);
        try {
            const { data } = await api.get("/presenze/me/week", { params: s ? { start: s } : {} });
            setData(data);
        } catch { setData(null); } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(start); }, [start, load]);

    if (!data || !data.has_assignments) return null;

    const today = data.today;
    const todayItems = data.items[today] || [];
    const isCurrentWeek = data.days.includes(today);

    const nav = (
        <div className="flex items-center gap-1">
            <button type="button" onClick={() => setStart(addDays(data.days[0], -7))} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800" title="Settimana precedente" data-testid="week-prev-btn">
                <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[110px] text-center font-mono-numbers text-xs font-semibold text-slate-700" data-testid="week-range">{fmtRange(data.days)}</span>
            <button type="button" onClick={() => setStart(addDays(data.days[0], 7))} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800" title="Settimana successiva" data-testid="week-next-btn">
                <ChevronRight className="h-4 w-4" />
            </button>
            {!isCurrentWeek && (
                <button type="button" onClick={() => setStart(null)} className="ml-1 rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50" data-testid="week-today-btn">Oggi</button>
            )}
        </div>
    );

    const listLayout = variant === "timbratura";
    const weekGrid = (
        <div className="relative">
            {loading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
            <div className={listLayout ? "space-y-2" : "grid gap-2 sm:grid-cols-7"} data-testid="week-grid">
                {data.days.map((d, i) => {
                    const items = data.items[d] || [];
                    const isToday = d === today;
                    if (listLayout && items.length === 0 && !isToday) {
                        return (
                            <div key={d} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/50 px-2.5 py-1.5 text-xs text-slate-400" data-testid={`week-day-${d}`}>
                                <span>{DAY_NAMES[i]} <span className="font-mono-numbers">{fmtDay(d)}</span></span><span>—</span>
                            </div>
                        );
                    }
                    return (
                        <div key={d} className={`rounded-lg border p-2 ${isToday ? "border-[#376DB3] bg-[#376DB3]/5 ring-1 ring-[#376DB3]/30" : "border-slate-100 bg-slate-50/50"}`} data-testid={`week-day-${d}`}>
                            <div className={`mb-1.5 flex items-baseline justify-between gap-2 text-xs font-bold ${isToday ? "text-[#376DB3]" : "text-slate-600"}`}>
                                <span className={listLayout ? "" : "sm:hidden"}>{DAY_NAMES[i]}</span>
                                {!listLayout && <span className="hidden sm:inline">{DAY_SHORT[i]}</span>}
                                <span className="font-mono-numbers font-semibold">{fmtDay(d)}{isToday ? " · oggi" : ""}</span>
                            </div>
                            {items.length === 0 ? (
                                <div className="py-2 text-center text-[11px] text-slate-300">—</div>
                            ) : (
                                <div className="space-y-1.5">{items.map((e) => <WeekEntry key={`${e.start}-${e.worksite_name}-${e.activity_name}`} e={e} dense={!listLayout} />)}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    if (variant === "timbratura") {
        return (
            <div className={`rounded-2xl bg-white/95 p-4 shadow-2xl ${className}`} data-testid="my-week-card">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        <CalendarDays className="h-3.5 w-3.5" /> {showWeek ? "La mia settimana" : "Oggi"}
                    </div>
                    <button type="button" onClick={() => { setShowWeek((v) => !v); if (showWeek) setStart(null); }}
                        className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-[#376DB3] hover:bg-[#376DB3]/5" data-testid="week-toggle-btn">
                        {showWeek ? "Solo oggi" : "Vedi settimana"}
                    </button>
                </div>
                {showWeek ? (
                    <div className="mt-3 space-y-3">
                        <div className="flex justify-center">{nav}</div>
                        {weekGrid}
                    </div>
                ) : todayItems.length === 0 ? (
                    <div className="mt-3 flex items-center gap-2 text-sm text-slate-500" data-testid="week-today-empty"><MapPin className="h-4 w-4" /> Nessun cantiere previsto per oggi</div>
                ) : (
                    <div className="mt-3 space-y-2" data-testid="week-today-list">{todayItems.map((e) => <WeekEntry key={`${e.start}-${e.worksite_name}-${e.activity_name}`} e={e} />)}</div>
                )}
            </div>
        );
    }

    return (
        <div className={`mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm ${className}`} data-testid="my-week-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <CalendarDays className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="font-display text-base font-bold text-slate-800">La mia settimana</h2>
                        <p className="text-xs text-slate-500">Cantieri, giorni e orari previsti{todayItems.length ? ` · oggi ${todayItems.length} intervent${todayItems.length === 1 ? "o" : "i"}` : ""}</p>
                    </div>
                </div>
                {nav}
            </div>
            <div className="mt-4">{weekGrid}</div>
        </div>
    );
}
